import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { SingleHorizontalOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Accordion } from '@connected/ui/components/accordion';
import { Section } from '@connected/ui/features/layouts';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { Device } from '@/hooks/queries/use-devices';

import { RisoMeasurementList } from '@/components/features/riso-measurement';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useCondition } from '@/config/conditions';
import { useDevices } from '@/hooks/queries/use-devices';
import { useRisoMeasurements } from '@/hooks/queries/use-riso-measurements';

// Blue-Green Deployment: Toggle between read-only (false) and editable (true) measurements
const USE_EDITABLE_MEASUREMENTS = true;
const DEVICE_KIND_OPTIONS = ['FinalCircuitProtectiveDevice', 'ResidualCurrentCircuitBreakerDevice', 'All'] as const;

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/riso/',
)({
    component: DistributionBoardRisoPage,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
                electricalProjectVersionId: params.projectVersionId,
            }),
        );
    },
    validateSearch: z.object({
        deviceKind: z.enum(DEVICE_KIND_OPTIONS).default('All'),
    }),
});

function DistributionBoardRisoPage() {
    const { t } = useTranslation('re-inspections');

    const canAccessResidualCurrentCircuitBreakers = useCondition('canAccessResidualCurrentCircuitBreakers');

    const params = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const { data: { devices } } = useDevices({
        electricalProjectVersionId: params.projectVersionId,
        distributionBoardId: params.distributionboardId,
        deviceKind: canAccessResidualCurrentCircuitBreakers
            ? search.deviceKind === 'All'
                ? ['FinalCircuitProtectiveDevice', 'ResidualCurrentCircuitBreakerDevice']
                : [search.deviceKind]
            : ['FinalCircuitProtectiveDevice'],
    });

    return (
        <Section>
            <Section.Header>
                <Section.Header.Title>{t('distribution-box.riso.title')}</Section.Header.Title>
                {canAccessResidualCurrentCircuitBreakers && (
                    <Section.Header.Actions>
                        <SingleOptionField
                            name="deviceKind"
                            className="flex flex-row items-center justify-between"
                            handleChange={(deviceKind) => {
                                navigate({
                                    to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/riso',
                                    params,
                                    search: { deviceKind },
                                });
                            }}
                            state={{ value: search.deviceKind }}
                            options={DEVICE_KIND_OPTIONS.map(option => ({ label: t(`distribution-box.riso.deviceKind.${option}`), value: option }))}
                            render={SingleHorizontalOptionItem}
                        />
                    </Section.Header.Actions>
                )}
            </Section.Header>
            <Section.Content className="py-0 px-4">
                <Accordion
                    type="single"
                    collapsible
                    defaultValue={devices[0]?.ElectricalProjectDistributionBoardDeviceId ?? undefined}
                >
                    {devices.map(device => (
                        <Accordion.Item key={device.ElectricalProjectDistributionBoardDeviceId} value={device.ElectricalProjectDistributionBoardDeviceId!}>
                            <Accordion.Trigger id={device.ElectricalProjectDistributionBoardDeviceId ?? undefined}>
                                <DeviceLabel
                                    device={device}
                                    showDeviceKindPrefix={canAccessResidualCurrentCircuitBreakers && search.deviceKind === 'All'}
                                />
                            </Accordion.Trigger>
                            <Accordion.Content>
                                {USE_EDITABLE_MEASUREMENTS
                                    ? (
                                            <RisoMeasurementList
                                                electricalProjectVersionId={params.projectVersionId}
                                                distributionBoardDeviceId={device.ElectricalProjectDistributionBoardDeviceId!}
                                            />
                                        )
                                    : (
                                            <RisoMeasurementsReadOnly device={device} />
                                        )}
                            </Accordion.Content>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </Section.Content>
            <Section.Footer>
                <FooterNavigation />
            </Section.Footer>
        </Section>
    );
}

type DeviceLabelProps = {
    device: Device;
    showDeviceKindPrefix: boolean;
};

function DeviceLabel({ device, showDeviceKindPrefix }: DeviceLabelProps) {
    return (
        <>
            {showDeviceKindPrefix && device.DeviceKind === 'ResidualCurrentCircuitBreakerDevice'
                ? 'Automaat - '
                : null}
            {showDeviceKindPrefix && device.DeviceKind === 'FinalCircuitProtectiveDevice'
                ? 'Groep - '
                : null}
            {device.Identifier ? `${device.Identifier} ` : 'Onbekend '}
            {device.TrippingCharacteristic ? `- ${device.TrippingCharacteristic} ` : null}
            {device.BreakerRatedCurrent ? `- ${device.BreakerRatedCurrent} A` : null}
        </>
    );
}

type RisoMeasurementsReadOnlyProps = {
    device: Device;
};

/**
 * Blue deployment: Read-only measurements display
 * @deprecated Will be removed once USE_EDITABLE_MEASUREMENTS is permanently enabled
 */
function RisoMeasurementsReadOnly({ device }: RisoMeasurementsReadOnlyProps) {
    const params = Route.useParams();

    const { data: { measurements } } = useRisoMeasurements({
        electricalProjectVersionId: params.projectVersionId,
        distributionBoardDeviceId: device.ElectricalProjectDistributionBoardDeviceId ?? '',
    });

    if (measurements.length === 0) {
        return (
            <div className="flex flex-col gap-1">
                <div className="grid grid-cols-4 gap-2">
                    <p className="text-start px-1 font-bold">Gemeten waarde (Ω)</p>
                    <p className="text-start px-1 font-bold">Minimale waarde</p>
                    <p className="text-start px-1 font-bold">Akkoord</p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                    <p className="text-start px-1">-</p>
                    <p className="text-start px-1">-</p>
                    <p className="text-start px-1">-</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1">
            <div className="grid grid-cols-4 gap-2">
                <p className="text-start px-1 font-bold">Gemeten waarde (Ω)</p>
                <p className="text-start px-1 font-bold">Minimale waarde</p>
                <p className="text-start px-1 font-bold">Akkoord</p>
            </div>
            {measurements.map(measurement => (
                <div key={measurement.ElectricalProjectRisoMeasurementId} className="grid grid-cols-4 gap-2">
                    <p>{measurement.MeasuredValue}</p>
                    <p>{measurement.MinimumValue}</p>
                    <p>{measurement.IsFaulty ? 'Nee' : 'Ja'}</p>
                </div>
            ))}
        </div>
    );
}
